import BasePeripheral from '@appblocks/codegeneration/Peripherals/_BasePeripheral';
import {
    SettingType, Setting, BlockType, SettingState,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Peripherals from '@appblocks/codegeneration/Peripherals';

export default class Ser232 extends BasePeripheral {
    constructor() {
        super();
        this.id = 'ser232';
        this.devices = ['WS1102'];
        this.name = 'IO Expander RS232';
        this.requirements = ['tios'];
        this.capabilities = ['serial'];
        this.fileLinks = [
            // 'ssi\\ssi.tbs',
            // 'ssi\\ssi.tbh',
        ];
        this.files = [
            'libraries/super_i2c/super_i2c.tbh',
            'libraries/super_i2c/super_i2c.tbs',
            'libraries/tibbits/tbt41/tbt41.tbh',
            'libraries/tibbits/tbt41/tbt41.tbs',
            'libraries/ssi/ssi.tbh',
            'libraries/ssi/ssi.tbs',
        ];
        const settings: Array<Setting> = [
            {
                id: 'PORT',
                name: 'Port',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'TX',
                name: 'TX',
                settingType: SettingType.PIN,
                meta: 'TX',
                required: true,
                default: '',
            },
            {
                id: 'RX',
                name: 'RX',
                settingType: SettingType.PIN,
                meta: 'RX',
                required: true,
                default: '',
            },
            {
                id: 'RTS',
                name: 'RTS',
                settingType: SettingType.PIN,
                meta: 'RTS',
                required: true,
                default: '',
            },
            {
                id: 'CTS',
                name: 'CTS',
                settingType: SettingType.PIN,
                meta: 'CTS',
                required: true,
                default: '',
            },
            {
                id: 'DTR',
                name: 'DTR',
                settingType: SettingType.PIN,
                meta: 'DTR',
                required: true,
                default: '',
            },
            {
                id: 'DSR',
                name: 'DSR',
                settingType: SettingType.PIN,
                meta: 'DSR',
                required: true,
                default: '',
            },
            {
                id: 'BAUD',
                name: 'Baud Rate',
                settingType: SettingType.OPTION,
                required: true,
                default: '9600',
                linkToSetting: true,
                options: [
                    {
                        name: '1200',
                        value: '1200',
                    },
                    {
                        name: '2400',
                        value: '2400',
                    },
                    {
                        name: '4800',
                        value: '4800',
                    },
                    {
                        name: '9600',
                        value: '9600',
                    },
                    {
                        name: '19200',
                        value: '19200',
                    },
                    {
                        name: '38400',
                        value: '38400',
                    },
                    {
                        name: '57600',
                        value: '57600',
                    },
                    {
                        name: '115200',
                        value: '115200',
                    },
                    {
                        name: '230400',
                        value: '230400',
                    },
                    {
                        name: '460800',
                        value: '460800',
                    },
                    {
                        name: '921600',
                        value: '921600',
                    },
                ],
                setting: {
                    SETTINGID: 'BAUD',
                    NAME: 'Baud Rate',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 8,
                    DEFAULT: '9600',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: '1200',
                            value: '1200',
                        },
                        {
                            name: '2400',
                            value: '2400',
                        },
                        {
                            name: '4800',
                            value: '4800',
                        },
                        {
                            name: '9600',
                            value: '9600',
                        },
                        {
                            name: '19200',
                            value: '19200',
                        },
                        {
                            name: '38400',
                            value: '38400',
                        },
                        {
                            name: '57600',
                            value: '57600',
                        },
                        {
                            name: '115200',
                            value: '115200',
                        },
                        {
                            name: '230400',
                            value: '230400',
                        },
                        {
                            name: '460800',
                            value: '460800',
                        },
                        {
                            name: '921600',
                            value: '921600',
                        },
                    ],
                },
            },
            {
                id: 'FLOWCONTROL',
                name: 'Flow Control',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                linkToSetting: true,
                options: [
                    {
                        name: 'disabled',
                        value: '0',
                    },
                    {
                        name: 'RTSCTS',
                        value: '1',
                    },
                    {
                        name: 'XONOFF',
                        value: '2',
                    },
                ],
                setting: {
                    SETTINGID: 'FLOWCTRL',
                    NAME: 'Flow Control',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 1,
                    MAX: 1,
                    DEFAULT: '0',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'disabled',
                            value: '0',
                        },
                        {
                            name: 'RTSCTS',
                            value: '1',
                        },
                        {
                            name: 'XONOFF',
                            value: '2',
                        },
                    ],
                },
            },
            {
                id: 'PARITY',
                name: 'Parity',
                settingType: SettingType.OPTION,
                required: true,
                linkToSetting: true,
                default: '0',
                options: [
                    {
                        name: 'None',
                        value: '0',
                    },
                    {
                        name: 'Even',
                        value: '1',
                    },
                    {
                        name: 'Odd',
                        value: '2',
                    },
                    {
                        name: 'Mark (Always 1)',
                        value: '3',
                    },
                    {
                        name: 'Space (Always 0)',
                        value: '4',
                    },
                ],
                setting: {
                    SETTINGID: 'PARITY',
                    NAME: 'Parity',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 1,
                    MAX: 1,
                    DEFAULT: '0',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'None',
                            value: '0',
                        },
                        {
                            name: 'Even',
                            value: '1',
                        },
                        {
                            name: 'Odd',
                            value: '2',
                        },
                        {
                            name: 'Mark(Always 1)',
                            value: '3',
                        },
                        {
                            name: 'Space(Always 0)',
                            value: '4',
                        },
                    ],
                },
            },
            {
                id: 'BITS',
                name: 'Data Bits',
                settingType: SettingType.OPTION,
                required: true,
                linkToSetting: true,
                default: '1',
                options: [
                    {
                        name: '7 Bits',
                        value: '0',
                    },
                    {
                        name: '8 Bits',
                        value: '1',
                    },
                ],
                setting: {
                    SETTINGID: 'BITS',
                    NAME: 'Data Bits',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 1,
                    MAX: 1,
                    DEFAULT: '1',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: '7 Bits',
                            value: '0',
                        },
                        {
                            name: '8 Bits',
                            value: '1',
                        },
                    ],
                },
            },
            {
                id: 'TXBUF',
                name: 'TX Buffer Pages',
                settingType: SettingType.BUFFER,
                required: true,
                default: '1',
            },
            {
                id: 'RXBUF',
                name: 'RX Buffer Pages',
                settingType: SettingType.BUFFER,
                required: true,
                default: '1',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getCodeBlocks() {
        const blocks = [];
        const FLOWCONTROL = this.getSettingValue('FLOWCONTROL');
        const serName = `SER_${this.getSettingValue('NAME')}`;
        const NAME = this.getSettingValue('NAME');
        const project = this.project;
        let flowControlMap = '';
        if (FLOWCONTROL === '1') { // RTSCTS
            let interruptEnum = '0';
            try {
                const interruptNumber = Peripherals.getInterrupt(this.getSettingValue('CTS'), this.project.device.id);
                interruptEnum = PlatformUtils.getEnum(interruptNumber, 'pl_int_num', this.project.device.platform);
            } catch (ex) {
                //
            }
            flowControlMap = `
    ser.rtsmap=PL_IO_NUM_3_RTS_INT1
    io.num=PL_IO_NUM_3_RTS_INT1
    io.enabled=YES
    io.state=LOW
    ser.ctsmap=PL_INT_NUM_0
    io.num=PL_IO_NUM_2_CTS_INT0
    io.enabled=NO`;
        }
        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    'serial port setup for port ${Number(this.getSettingValue('PORT'))}
    ser.num=${serName}
    ser.enabled=NO
    init_io_expander()
    tbt41_line_set( SER0_HDFD,  HIGH , S1_CHANNEL)
    tbt41_line_out_enable( SER0_HDFD, YES , S1_CHANNEL)
    tbt41_line_set( SER0_RSMD, LOW , S1_CHANNEL)
    tbt41_line_out_enable( SER0_RSMD, YES , S1_CHANNEL)
    ser.interface=PL_SER_SI_FULLDUPLEX
    ser.parity=${this.project.convertDataType(this.getSettingValue('PARITY'), TibboDataType.STRING, TibboDataType.BYTE)}
    ser.bits=${this.project.convertDataType(this.getSettingValue('BITS'), TibboDataType.STRING, TibboDataType.BYTE)}
    ser.flowcontrol=${this.project.convertDataType(this.getSettingValue('FLOWCONTROL'), TibboDataType.STRING, TibboDataType.BYTE)}
    ser.baudrate=ser.div9600*9600/${this.project.convertDataType(this.getSettingValue('BAUD'), TibboDataType.STRING, TibboDataType.DWORD)}
    ${flowControlMap}
    ser.esctype=PL_SER_ET_DISABLED
    ser.interchardelay=0
    ser.txbuffrq(${this.getSettingValue('TXBUF')})
    ser.rxbuffrq(${this.getSettingValue('RXBUF')})
    sys.buffalloc()
    ser.enabled=YES

    `,
        });
        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const ${serName}=${this.getSettingValue('PORT')}
const SER0_HDFD=5 'EXT_LINE_6 HIGH for full-duplex, LOW for half-duplex
const SER0_RSMD=6'EXT_LINE_7 HIGH for RS422/485, LOW for RS232
const S1_CLK = PL_IO_NUM_5_EXPANDER_SCL
const S1_DATA = PL_IO_NUM_6_EXPANDER_SDA
const S1_CHANNEL = 0
            `,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            id: 'declare_init_io_expander',
            blockType: BlockType.CODE,
            template: `
declare sub init_io_expander()
    `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.LOCATION,
            id: 'init_io_expander',
            template: `
sub init_io_expander()
    'Tibbit 41 setup on socket 1
    '================================================================
    select case tbt41_init("${serName}", YES,S1_CLK,S1_DATA,S1_CHANNEL)
    case TBT41_SELF_TEST_FAIL:
        sys.debugprint("TBT41 self test failed!\\r\\n")
        sys.halt()

    case TBT41_NO_SSI_AVAILABLE:
        sys.debugprint("SSI channel could not be allocated, using bit banging instead.\\r\\n")
    end select 
    '-----------------------------------------------------------------
end sub
    `,
        });

        blocks.push({
            location: 'on_ser_data_arrival',
            blockType: BlockType.CODE,
            template: `
    if ser.num=${serName} then
        'data on serial port ${this.getSettingValue('NAME')}
    end if
        `,
        });

        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/object_ser';
    }
}
